/**
 * PAGE: LISTE ÉCOLES (VERSION REFACTORISÉE)
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  ColumnSelectorModal,
  CreateSchoolModal,
  FiltersModal,
  ImportCSVModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { SchoolSidePanel } from '../components/panels/SchoolSidePanel';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, ActionsCell, EmptyCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import { BulkActionsBar, ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { useEntityList } from '../hooks/useEntityList';
import { useSchools } from '../hooks/useSchools';
import { useSupabase } from '../hooks/useSupabase';
import { useOpenFromSearch } from '../hooks/useOpenFromSearch';
import { usePermissions } from '../hooks/usePermissions';
import type { UpdateSchoolInput } from '../schemas/school';
import type { School } from '../types/school';
import { SCHOOL_CONFIG } from '../config/entities/school.config';
import {
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateFilterConfigs,
  generateColumnOptions,
  generateInitialColumnVisibility,
} from '../lib/entity-config';

export function SchoolsListPage() {
  const supabase = useSupabase();
  const {
    data: schools,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refreshSchools,
  } = useSchools();

  // Permissions utilisateur
  const { canCreateEntity, canDeleteEntity, canImportData, canExportData, canDeleteBulk } =
    usePermissions();

  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedSchool(null);
  }, []);

  // Générer automatiquement depuis SCHOOL_CONFIG
  const filterConfigs = useMemo(() => generateFilterConfigs(SCHOOL_CONFIG), []);
  const csvColumns = useMemo(() => generateCSVColumns(SCHOOL_CONFIG), []);

  // Générer les transformers de base + ajouter le custom pour contacts
  const csvTransformers = useMemo(() => {
    const baseTransformers = generateCSVTransformers(SCHOOL_CONFIG);
    return {
      ...baseTransformers,
      contacts: (value: any) => {
        const count = value?.[0]?.count ?? 0;
        return String(count);
      },
    };
  }, []);

  const importConfig = useMemo(() => generateImportConfig(SCHOOL_CONFIG), []);
  const initialColumnVisibility = useMemo(() => generateInitialColumnVisibility(SCHOOL_CONFIG), []);

  const entity = useEntityList({
    data: schools,
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: refreshSchools,
    entityName: SCHOOL_CONFIG.entityName,
    entityNamePlural: SCHOOL_CONFIG.entityNamePlural,
    searchableFields: ['name', 'city', 'country'] as (keyof School)[],
    filterConfigs,
    initialColumnVisibility, // ✅ Visibilité initiale des colonnes
    csvFileName: 'ecoles.csv',
    csvColumns,
    csvTransformers,
    importConfig,
    onDeleteSuccess: handleCloseSidePanel,
  });

  const { handleDeleteClick } = entity;

  const handleOpenSidePanel = useCallback((school: School) => {
    setSelectedSchool(school);
    setIsSidePanelOpen(true);
  }, []);

  // Ouvrir automatiquement le side panel depuis la recherche globale
  useOpenFromSearch({
    data: schools,
    getId: (s) => s.id,
    onOpen: handleOpenSidePanel,
    loading,
  });

  const handleUpdateSchool = useCallback(
    async (id: string, data: UpdateSchoolInput) => {
      if (!update) return;

      try {
        await update(id, data);
        // Rafraîchir la liste pour récupérer les relations mises à jour
        await refreshSchools();
        // Mettre à jour le selectedSchool avec les nouvelles données
        if (selectedSchool?.id === id) {
          const updatedSchool = schools.find((s) => s.id === id);
          if (updatedSchool) {
            setSelectedSchool(updatedSchool);
          }
        }
        toast.success('École mise à jour avec succès');
      } catch (error) {
        console.error('Error updating school:', error);
        toast.error('Erreur lors de la mise à jour');
      }
    },
    [update, selectedSchool, refreshSchools, schools]
  );

  const handleDeleteFromPanel = useCallback(
    async (id: string) => {
      if (selectedSchool) {
        entity.handleDeleteClick(id, selectedSchool.name);
      }
    },
    [selectedSchool, entity]
  );

  // Vérifier si des actions sont possibles
  const hasAnyBulkAction = canDeleteBulk('school') || canExportData('school');

  const columns: ColumnDef<School>[] = useMemo(
    () => [
      // Colonne checkbox uniquement si actions de masse possibles
      ...(hasAnyBulkAction
        ? ([
            {
              id: 'select',
              size: 60,
              header: ({ table }: any) => (
                <CheckboxCell
                  row={
                    {
                      getIsSelected: () => table.getIsAllRowsSelected(),
                      toggleSelected: () => table.toggleAllRowsSelected(),
                    } as any
                  }
                />
              ),
              cell: ({ row }: any) => <CheckboxCell row={row} />,
            },
          ] as ColumnDef<School>[])
        : []),
      {
        accessorKey: 'name',
        header: "Nom de l'école",
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'postal_code',
        header: 'Code Postal',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        enableSorting: true,
      },
      {
        accessorKey: 'city',
        header: 'Ville',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        enableSorting: true,
      },
      {
        accessorKey: 'country',
        header: 'Pays',
        cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
        enableSorting: true,
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        enableSorting: true,
      },
      {
        id: 'contacts_count',
        header: 'Contacts',
        cell: ({ row }) => {
          const count = row.original.contacts?.[0]?.count ?? 0;
          return <TableCell>{count}</TableCell>;
        },
        enableSorting: false,
      },
      // Colonnes additionnelles (masquées par défaut, disponibles dans le sélecteur)
      {
        accessorKey: 'positioning',
        header: 'Positionnement',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        size: 150,
      },
      {
        accessorKey: 'contact_first_name',
        header: 'Prénom contact',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        size: 150,
      },
      {
        accessorKey: 'contact_last_name',
        header: 'Nom contact',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        size: 150,
      },
      {
        accessorKey: 'contact_email',
        header: 'Email contact',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        size: 200,
      },
      {
        accessorKey: 'contact_phone',
        header: 'Téléphone contact',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        size: 150,
      },
      {
        accessorKey: 'address',
        header: 'Adresse',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string | null} />,
        size: 200,
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row }) => (
          <div className="px-3 py-2 max-w-48 truncate text-sm" title={row.original.notes || ''}>
            {row.original.notes || '-'}
          </div>
        ),
        size: 200,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => (
          <ActionsCell
            onView={() => handleOpenSidePanel(row.original)}
            onDelete={() => handleDeleteClick(row.original.id, row.original.name)}
            canDelete={canDeleteEntity('school')}
          />
        ),
        size: 120,
      },
    ],
    [handleOpenSidePanel, handleDeleteClick, canDeleteEntity, hasAnyBulkAction]
  );

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(SCHOOL_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  if (entity.loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Écoles' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <LoadingState message="Chargement des écoles..." />
      </PageLayout>
    );
  }

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Écoles' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des écoles"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <>
          <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Écoles' }]} />
          <div className="w-full h-px bg-zinc-200" />
        </>
      }
      sidePanel={
        selectedSchool && (
          <SchoolSidePanel
            isOpen={isSidePanelOpen}
            onClose={handleCloseSidePanel}
            school={selectedSchool}
            onUpdate={handleUpdateSchool}
            onDelete={handleDeleteFromPanel}
          />
        )
      }
    >
      <PageContent
        title="Écoles"
        description="Gérez les établissements scolaires"
        actions={
          <ActionButtons
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('school') ? entity.handleExportCSV : undefined}
            onUpload={canImportData('school') ? () => entity.setIsImportModalOpen(true) : undefined}
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={
              canCreateEntity('school') ? () => entity.setIsCreateModalOpen(true) : undefined
            }
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateSchoolModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${SCHOOL_CONFIG.entityNamePlural}`
            : `Supprimer l'${SCHOOL_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedItems.length} ${SCHOOL_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer "${entity.deleteConfirm.name}" ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: school, error: supabaseError } = await supabase
            .from('schools')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return school;
        }}
        onUpdate={update}
        existingData={schools}
        entityName={SCHOOL_CONFIG.entityName}
        entityNamePlural={SCHOOL_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={entity.table.selectedItems.length}
          entityNamePlural={SCHOOL_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={false}
          canDelete={canDeleteBulk('school')}
          canExport={canExportData('school')}
        />
      )}
    </PageLayout>
  );
}
